import { blocksOn, formPages, layoutOf, blockComponent } from '@ayumia/component-runtime';
import { text } from '../../app/i18n';
import { blockRegion, sectionRegion } from '../../work/FormLayoutParts';
import type { CanvasNode, CanvasProfile, NodeActions, ProfilePanel, PublishWarnings } from '../editor/canvas-types';
import { getAt, insertAt, moveWithin, removeAt } from '../editor/json-pointer';
import { FORM_PREVIEW_FORMATS } from './forms-config';
import { classifyTemplate, freshId, localFindings, TYPE_ICONS, TYPE_LABELS } from './forms-model';
import type { ConformityRules } from '../../work/form-targets';
import { FormCanvas, FORM_STATES } from './form-canvas';
import { blocksOf, declaredPages, duplicateBlock, moveBlock, movePage, moveSectionToPage, removeBlock, setPlacement, updateBlock, updateLayout, type Block, type Template } from './layout-model';
import { componentByName, currentLayout, flutterSupported, fr, layoutFindings, refusalOf, type EditorRegistry } from './layout-registry';
import { EMPTY_CATALOG, LIBRARY_GROUP_ICONS, LIBRARY_GROUPS, libraryV2, type StudioCatalog } from './v2/library';
import { deletePageV2 } from './v2/pages';
import { LANGUAGE_NAMES, languageName, localeCoverage, NO_SUGGESTION_REASON, templateLocales } from './v2/locales';
import type { InspectorFactory } from './form-inspector';

/**
 * Profil « formulaire » du socle d'éditeur sur canevas (ADR-146 T3) : comment un gabarit se lit en calques
 * (pages › zones › parties et blocs › champs), ce que la bibliothèque insère et où, ce que la barre de
 * sélection fait de chaque élément. La recette du poste refuse à l'insertion et au déplacement, avec le
 * motif du calcul du serveur ; elle n'est jamais affichée.
 */

type Section = Template['sections'][number];
type Entry = Section['fields'][number];


const sectionKind = (section: Section): string => (section.repeat !== undefined ? 'Partie répétée' : section.series !== undefined ? 'Liste de lignes' : 'Partie');

function fieldNodes(section: Section, s: number): CanvasNode[] {
  const nodes = section.fields.map((entry, f): CanvasNode => fieldNode(entry, `/sections/${s}/fields/${f}`));
  for (const [f, entry] of (section.repeat?.children?.fields ?? []).entries()) nodes.push(fieldNode(entry, `/sections/${s}/repeat/children/fields/${f}`));
  return nodes;
}

/**
 * Nature d'un champ en clair (ADR-146 addendum (15)) : ce qu'il saisit pour l'auteur (« Mesure », « Photo »,
 * « Date »), jamais un glyphe ni un type technique.
 */
export function fieldNature(entry: Entry): string {
  const widget = (entry.presentation as { widget?: string } | undefined)?.widget;
  if (widget === 'photo') return 'Photo';
  if (widget === 'signature') return 'Signature';
  // Champ « Personne » (ADR-152) : une référence, mais l'auteur choisit une personne, pas un objet.
  if (widget === 'person' && entry.field.type === 'reference') return 'Personne';
  if (entry.target !== undefined) return 'Mesure';
  if (entry.compute !== undefined) return 'Calcul';
  return TYPE_LABELS[entry.field.type] ?? 'Champ';
}

/** Icône du design system d'un champ (registre `Icon`), selon sa nature. */
export function fieldIcon(entry: Entry): string {
  const widget = (entry.presentation as { widget?: string } | undefined)?.widget;
  if (widget === 'photo') return 'image';
  if (widget === 'signature') return 'signature';
  if (widget === 'person' && entry.field.type === 'reference') return 'user';
  if (entry.target !== undefined) return 'thermometer';
  return TYPE_ICONS[entry.field.type] ?? 'text';
}

function fieldNode(entry: Entry, pointer: string): CanvasNode {
  const marks: string[] = [];
  if (entry.field.required === true) marks.push('*');
  const conditional = entry.visible_when !== undefined;
  return { id: `field:${pointer}`, label: text(entry.field.label, 'fr', 'Champ sans nom'), kind: fieldNature(entry), icon: fieldIcon(entry), badges: marks, ...(conditional ? { conditional: true } : {}), children: [] };
}

function sectionNode(template: Template, section: Section): CanvasNode {
  const s = template.sections.indexOf(section);
  return { id: `section:${section.section_id}`, label: text(section.label, 'fr', 'Partie sans nom'), kind: sectionKind(section), icon: section.series !== undefined ? 'table' : section.repeat !== undefined ? 'refresh' : 'rows', ...(section.visible_when !== undefined ? { conditional: true } : {}), insertTarget: `section:${section.section_id}`, children: fieldNodes(section, s) };
}

function blockNode(registry: EditorRegistry, block: Block): CanvasNode {
  const component = componentByName(registry, blockComponent(block).name);
  const label = fr(block.label, fr(component?.manifest.component.label, 'Bloc'));
  return { id: `block:${block.block_id}`, label, kind: 'Bloc', icon: 'boxes', ...(block.visible_when !== undefined ? { conditional: true } : {}), ...(component !== undefined && !flutterSupported(component) ? { muted: 'non pris en charge sur la tablette' } : {}), children: [] };
}

/** Calques : sans mise en page, les parties ; avec, les pages, leurs zones, puis les blocs de toutes les pages. */
export function formTree(registry: EditorRegistry, template: Template): CanvasNode {
  const root: CanvasNode = { id: 'form', label: text(template.label, 'fr', 'Formulaire'), kind: 'Formulaire', icon: 'clipboard', children: [] };
  const layout = currentLayout(registry, template);
  const header: CanvasNode = { id: 'header', label: 'Bandeau du haut', kind: 'Bandeau', icon: 'id-card', children: [] };
  const footer: CanvasNode = { id: 'footer', label: 'Barre du bas', kind: 'Barre', icon: 'rows', ...((template.layout?.footer as { hidden?: boolean } | undefined)?.hidden === true ? { muted: 'masquée sur l’appareil' } : {}), children: [] };
  if (layoutOf(template) === undefined || layout === undefined) {
    root.children = [header, ...template.sections.map((section) => sectionNode(template, section)), footer];
    return root;
  }
  const body = layout.body;
  const declared = declaredPages(template).length > 0;
  // Calques (ADR-146 addendum (15)) : chaque bloc à sa zone ; un bloc de toutes les pages une seule fois, sous
  // « Sur toutes les pages », à sa zone. Une zone vide n'est pas listée, sauf la première qui reçoit des parties
  // d'une page vide.
  const persistent = (block: { page_ids?: readonly string[] }): boolean => declared && block.page_ids === undefined;
  const regionsOf = (pageId: string, sectionIds: readonly string[]): CanvasNode[] => {
    const all = body.regions.map((region): CanvasNode => ({
      id: `region:${pageId}:${region.region_id}`,
      label: fr(region.label, 'Zone'),
      kind: 'Zone',
      heading: true,
      insertTarget: `region:${region.region_id}`,
      children: [
        ...template.sections.filter((section) => sectionIds.includes(section.section_id) && sectionRegion(body, layoutOf(template)?.placements?.[section.section_id]?.region) === region.region_id).map((section) => sectionNode(template, section)),
        ...blocksOn(template, pageId).filter((block) => !persistent(block) && blockRegion(body, block.region) === region.region_id).map((block) => blockNode(registry, block)),
      ],
    }));
    const filled = all.filter((region) => region.children.length > 0);
    if (filled.length > 0) return filled;
    const first = body.regions.findIndex((region) => region.accepts.includes('section'));
    return first < 0 ? [] : [all[first]!];
  };
  root.children.unshift(header);
  const pagesNode: CanvasNode = { id: 'pages', label: 'Pages du formulaire', kind: 'Conteneur de pages', icon: 'layers', children: [] };
  for (const page of formPages(template)) {
    if (!declared) root.children.push(...regionsOf(page.page_id, page.sections));
    else pagesNode.children.push({ id: `page:${page.page_id}`, label: fr(page.label, 'Page'), kind: 'Page', icon: 'file', children: regionsOf(page.page_id, page.sections) });
  }
  if (declared) root.children.push(pagesNode);
  const everywhere = blocksOf(template).filter(persistent);
  if (everywhere.length > 0) {
    const zones = body.regions
      .map((region): CanvasNode => ({ id: `region:persistent:${region.region_id}`, label: fr(region.label, 'Zone'), kind: 'Zone', heading: true, children: everywhere.filter((block) => blockRegion(body, block.region) === region.region_id).map((block) => blockNode(registry, block)) }))
      .filter((zone) => zone.children.length > 0);
    root.children.push({ id: 'region:persistent', label: 'Sur toutes les pages', kind: 'Zone', heading: true, children: zones });
  }
  root.children.push(footer);
  return root;
}

const sectionIndex = (template: Template, id: string): number => template.sections.findIndex((section) => section.section_id === id);

/** Retire une partie et ses réglages de mise en page (placement, pages). */
function removeSection(template: Template, sectionId: string): Template {
  const s = sectionIndex(template, sectionId);
  const without = removeAt(template, `/sections/${s}`);
  if (layoutOf(without) === undefined) return without;
  let next = setPlacement(without, sectionId, { region: undefined, card: undefined, collapsible: undefined, columns: undefined, emphasis: undefined, size: undefined, readonly_when: undefined, presentation: undefined, entry_panel: undefined });
  next = updateLayout(next, (layout) => ({ ...layout, pages: layout.pages?.map((page) => ({ ...page, sections: page.sections.filter((id) => id !== sectionId) })).filter((page) => page.sections.length > 0) }));
  if ((next.layout?.pages as unknown[] | undefined)?.length === 0) next = updateLayout(next, (layout) => ({ ...layout, pages: undefined }));
  return next;
}

/** Ce que la barre de sélection propose pour un élément. */
export function formActions(registry: EditorRegistry, template: Template, id: string): NodeActions<Template> {
  const body = currentLayout(registry, template)?.body;
  const layered = layoutOf(template) !== undefined && body !== undefined;
  const pageParent = (sectionId: string): string => (declaredPages(template).length > 0 ? `page:${formPages(template).find((page) => page.sections.includes(sectionId))?.page_id ?? ''}` : 'form');
  if (id.startsWith('block:')) {
    const blockId = id.slice(6);
    const block = blocksOf(template).find((candidate) => candidate.block_id === blockId);
    if (block === undefined) return {};
    return {
      parent: block.page_ids?.length === 1 ? `page:${block.page_ids[0]}` : 'form',
      moveUp: () => moveBlock(template, blockId, -1),
      moveDown: () => moveBlock(template, blockId, 1),
      duplicate: () => {
        const copy = duplicateBlock(template, blockId);
        return { document: copy.template, id: `block:${copy.id}` };
      },
      remove: () => removeBlock(template, blockId),
      moveTo: (body?.regions ?? []).filter((region) => region.region_id !== block.region && region.role !== 'footer').map((region) => ({
        label: `Zone : ${fr(region.label)}`,
        apply: () => {
          const next = updateBlock(template, blockId, { region: region.region_id });
          const refused = refusalOf(registry, template, next);
          return refused === null ? next : { refused };
        },
      })),
      insertAfter: `after:block:${blockId}`,
    };
  }
  if (id.startsWith('section:')) {
    const sectionId = id.slice(8);
    const s = sectionIndex(template, sectionId);
    if (s < 0) return {};
    const placement = layoutOf(template)?.placements?.[sectionId];
    return {
      parent: pageParent(sectionId),
      ...(s > 0 ? { moveUp: () => moveWithin(template, '/sections', s, s - 1) } : {}),
      ...(s < template.sections.length - 1 ? { moveDown: () => moveWithin(template, '/sections', s, s + 1) } : {}),
      ...(template.sections.length > 1 ? { remove: () => removeSection(template, sectionId) } : {}),
      moveTo: [
        ...(layered ? body.regions.filter((region) => region.accepts.includes('section') && region.region_id !== sectionRegion(body, placement?.region)) : []).map((region) => ({
          label: `Zone : ${fr(region.label)}`,
          apply: () => {
            const next = setPlacement(template, sectionId, { region: region.region_id });
            const refused = refusalOf(registry, template, next);
            return refused === null ? next : { refused };
          },
        })),
        ...declaredPages(template).filter((page) => !page.sections.includes(sectionId)).map((page) => ({ label: `Page : ${fr(page.label)}`, apply: () => moveSectionToPage(template, sectionId, page.page_id) })),
      ],
      insertAfter: `after:section:${sectionId}`,
    };
  }
  if (id.startsWith('field:')) {
    const pointer = id.slice(6);
    const segments = pointer.split('/');
    const list = segments.slice(0, -1).join('/');
    const index = Number(segments[segments.length - 1]);
    const entries = (getAt(template, list) as Entry[] | undefined) ?? [];
    const section = template.sections[Number(segments[2])];
    if (entries[index] === undefined || section === undefined) return {};
    return {
      parent: `section:${section.section_id}`,
      ...(index > 0 ? { moveUp: () => moveWithin(template, list, index, index - 1) } : {}),
      ...(index < entries.length - 1 ? { moveDown: () => moveWithin(template, list, index, index + 1) } : {}),
      duplicate: () => {
        const source = entries[index]!;
        const copy = { ...JSON.parse(JSON.stringify(source)), field: { ...source.field, field_id: freshId(template, 'champ'), label: Object.fromEntries(Object.entries(source.field.label as Record<string, string>).map(([language, value]) => [language, `${value} (copie)`])) } } as Entry;
        return { document: insertAt(template, list, copy, index + 1), id: `field:${list}/${index + 1}` };
      },
      ...(entries.length > 1 ? { remove: () => removeAt(template, pointer) } : {}),
      insertAfter: `after:field:${pointer}`,
    };
  }
  if (id === 'header' || id === 'footer' || id === 'pages') return { parent: 'form' };
  if (id.startsWith('page:')) {
    const pageId = id.slice(5);
    const pages = declaredPages(template);
    const index = pages.findIndex((page) => page.page_id === pageId);
    if (index < 0) return {};
    return {
      parent: 'pages',
      ...(index > 0 ? { moveUp: () => movePage(template, pageId, -1) } : {}),
      ...(index < pages.length - 1 ? { moveDown: () => movePage(template, pageId, 1) } : {}),
      // Supprimer une page laisse ses parties « À placer » (addendum (14)) ; la boîte du conteneur de pages peut aussi les supprimer.
      remove: () => deletePageV2(template, pageId, false),
    };
  }
  return {};
}

/** Constats du brouillon : contrôles locaux du gabarit puis ceux de la mise en page (calcul du serveur). */
export function formFindings(registry: EditorRegistry, template: Template) {
  return [...localFindings(template), ...layoutFindings(registry, template).map(({ severity, code, path, message }) => ({ severity, code, path, message }))];
}

/** Élément qui porte un chemin de constat (champ, partie, bloc, page), sinon le formulaire. */
export function formNodeOf(template: Template, path: string): string {
  const field = /^\/sections\/(\d+)\/(fields|repeat\/children\/fields)\/(\d+)/.exec(path);
  if (field !== null) return `field:/sections/${field[1]}/${field[2]}/${field[3]}`;
  const section = /^\/sections\/(\d+)/.exec(path);
  if (section !== null) {
    const found = template.sections[Number(section[1])];
    if (found !== undefined) return `section:${found.section_id}`;
  }
  const block = /^\/layout\/blocks\/(\d+)/.exec(path);
  if (block !== null) {
    const found = blocksOf(template)[Number(block[1])];
    if (found !== undefined) return `block:${found.block_id}`;
  }
  const page = /^\/layout\/pages\/(\d+)/.exec(path);
  if (page !== null) {
    const found = declaredPages(template)[Number(page[1])];
    if (found !== undefined) return `page:${found.page_id}`;
  }
  if (/^\/layout\/header(\/|$)/.test(path) || /^\/visual(\/|$)/.test(path)) return 'header';
  if (/^\/layout\/footer(\/|$)/.test(path)) return 'footer';
  if (/^\/layout\/(pages|navigation)$/.test(path)) return declaredPages(template).length > 0 ? 'pages' : 'form';
  const placement = /^\/layout\/placements\/([a-z][a-z0-9_]*)/.exec(path);
  if (placement !== null) return `section:${placement[1]}`;
  return 'form';
}

/** Bornes des règles partagées servies par le Studio, pour que l'aperçu montre la cible et non la référence de la règle. */
export function rulesOf(rules: ReadonlyArray<{ test_id: string; min?: number; max?: number; unit?: string }>): ConformityRules {
  return Object.fromEntries(rules.map(({ test_id, ...bounds }) => [test_id, bounds]));
}

/**
 * Avertissements de langue avant publication (addendum (14) : une langue incomplète ne bloque jamais) : la
 * couverture par langue, « Publier quand même » avec repli sur la langue par défaut, ou « Traduire puis publier »
 * par la capacité d'IA du Core, grisé avec son motif tant qu'elle n'est pas servie.
 */
export function languageWarnings(template: Template, options: { defaultLocale?: string; addedLocales?: readonly string[]; translate?: (template: Template) => Template | null } = {}): PublishWarnings<Template> | null {
  const defaultLocale = options.defaultLocale ?? 'fr';
  const coverage = localeCoverage(template, templateLocales(template, defaultLocale, options.addedLocales ?? [])).filter((entry) => entry.locale !== defaultLocale && entry.done < entry.total);
  if (coverage.length === 0) return null;
  const missing = coverage.reduce((total, entry) => total + entry.total - entry.done, 0);
  const translate = options.translate;
  return {
    title: `Langues incomplètes · ${coverage.length}`,
    badge: 'Non bloquant',
    lines: coverage.map((entry) => ({ label: languageName(entry.locale), detail: `${entry.done} sur ${entry.total}` })),
    fallback: `Repli sur ${LANGUAGE_NAMES[defaultLocale]?.fr ?? defaultLocale}`,
    fix: { label: 'Traduire puis publier', detail: `${missing} texte${missing > 1 ? 's' : ''} · marqués « suggérés »`, ...(translate === undefined ? { disabledReason: NO_SUGGESTION_REASON } : {}), run: (document) => translate?.(document) ?? null },
  };
}

export function formCanvasProfile(registry: EditorRegistry, inspector: InspectorFactory, panels: readonly ProfilePanel<Template>[], rules: ConformityRules = {}, catalog: StudioCatalog = EMPTY_CATALOG, languages: { defaultLocale?: string; addedLocales?: readonly string[] } = {}): CanvasProfile<Template> {
  const library = (template: Template) => libraryV2(registry, catalog, template);
  const extras = { tree: (template: Template) => formTree(registry, template), actions: (template: Template, id: string) => formActions(registry, template, id), library };
  return {
    noun: 'Formulaire',
    tree: (template) => formTree(registry, template),
    actions: (template, id) => formActions(registry, template, id),
    library,
    libraryGroups: LIBRARY_GROUPS,
    libraryGroupIcons: LIBRARY_GROUP_ICONS,
    publishWarnings: (template) => languageWarnings(template, languages),
    inspector,
    renderCanvas: (props) => <FormCanvas registry={registry} props={props} rules={rules} extras={extras} />,
    inlineToolbar: true,
    formats: FORM_PREVIEW_FORMATS,
    states: FORM_STATES,
    panels,
    localFindings: (template) => formFindings(registry, template),
    nodeOf: formNodeOf,
    classify: classifyTemplate,
  };
}
